import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useEffect } from "react";
import {
  Droplets, PhoneCall, KanbanSquare, Workflow, CalendarDays, BarChart3,
  CheckCircle2, ArrowRight, Zap, Shield, Clock, Star, Menu,
} from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1787672357497-9d9e025f01b3?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const DASH = "https://images.unsplash.com/photo-1785484267245-0311d4b76008?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const FEATURES = [
  { icon: PhoneCall, title: "Capture every lead", desc: "A public booking form turns website visitors into leads in your pipeline instantly — no missed calls, no lost jobs." },
  { icon: KanbanSquare, title: "Visual CRM pipeline", desc: "Drag leads from New to Job Completed. Table or Kanban — see exactly where every opportunity stands." },
  { icon: Workflow, title: "Automated follow-up", desc: "Send the first reply in seconds and nudge cold leads automatically. You set the steps, delays and messages." },
  { icon: CalendarDays, title: "Appointments & dispatch", desc: "Book jobs, assign techs, and view everything in a calendar or list. Linked right to the customer record." },
  { icon: BarChart3, title: "Owner-friendly analytics", desc: "Conversion rates, lead sources and opportunity value — the numbers that actually grow a plumbing business." },
  { icon: Shield, title: "Private & secure", desc: "Your data is isolated in your own workspace with role-based access for owners, admins and staff." },
];

const STEPS = [
  { n: "1", title: "Set up your workspace", desc: "Add your business details, services and hours in a quick onboarding wizard." },
  { n: "2", title: "Share your booking link", desc: "Drop your unique form link on your website, Google profile or ads to start collecting leads." },
  { n: "3", title: "Close more jobs", desc: "Auto follow-ups, a clear pipeline and reminders help you win the work before competitors call back." },
];

const FAQ = [
  { q: "Do I need any technical skills?", a: "No. PlumbFlow is built for plumbing business owners. You set it up in minutes with a guided wizard — no code, no integrations required to get started." },
  { q: "How does the lead capture form work?", a: "Every account gets a unique public link. Homeowners fill it out in seconds and the lead lands directly in your pipeline, triggering your follow-up automation." },
  { q: "Can my team use it too?", a: "Yes. Invite admins and staff with role-based permissions so everyone sees what they need and your data stays protected." },
  { q: "What does it cost?", a: "One simple plan at $79/month, with a free trial. No per-seat fees and no long-term contract." },
  { q: "Can I connect SMS and email providers later?", a: "Absolutely. The automation engine is built to plug into email and SMS providers, so you can scale your outreach when you're ready." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    document.title = "PlumbFlow — Lead CRM & Follow-up for Plumbing Businesses";
  }, []);

  const goApp = () => navigate(user ? "/app/dashboard" : "/register");

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-blue-600 flex items-center justify-center">
              <Droplets className="h-5 w-5 text-white" />
            </div>
            <span className="font-heading font-extrabold text-xl">PlumbFlow</span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="#features" className="hover:text-slate-900">Features</a>
            <a href="#how" className="hover:text-slate-900">How it works</a>
            <a href="#pricing" className="hover:text-slate-900">Pricing</a>
            <a href="#faq" className="hover:text-slate-900">FAQ</a>
          </nav>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate("/login")} data-testid="nav-login-btn">Log in</Button>
            <Button onClick={() => navigate("/register")} data-testid="nav-signup-btn" className="bg-blue-600 hover:bg-blue-700">
              Start free trial
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden grid-dots">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <Badge className="bg-blue-50 text-blue-700 border border-blue-200 mb-5">
              <Zap className="h-3.5 w-3.5 mr-1" /> Built for U.S. plumbing pros
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] font-heading">
              Stop losing jobs to <span className="text-blue-600">slow follow-up</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              PlumbFlow captures every lead, organizes them in a simple CRM, and follows up automatically — so you book
              more jobs without chasing your phone all day.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={goApp} data-testid="hero-cta-btn" className="bg-blue-600 hover:bg-blue-700 text-base h-12 px-7">
                Start free trial <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <Button size="lg" variant="outline" onClick={() => navigate("/login")} data-testid="hero-login-btn" className="h-12 px-7 text-base">
                Log in
              </Button>
            </div>
            <div className="mt-6 flex items-center gap-5 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> No credit card</span>
              <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-emerald-600" /> 5-minute setup</span>
            </div>
          </div>
          <div className="relative animate-fade-up">
            <div className="absolute -inset-4 bg-gradient-to-tr from-blue-600/20 to-amber-500/10 blur-2xl rounded-3xl" />
            <img src={HERO} alt="Plumbing professional" className="relative rounded-2xl shadow-2xl border border-slate-200 object-cover w-full h-[420px]" />
          </div>
        </div>
      </section>

      {/* Value prop stats */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {[["78%", "of jobs go to the first to respond"], ["<5 min", "average first-reply with automation"], ["1 link", "to capture leads anywhere"], ["$79/mo", "flat — no per-seat fees"]].map(([a, b]) => (
            <div key={b}>
              <div className="text-3xl lg:text-4xl font-extrabold font-heading text-blue-400">{a}</div>
              <div className="text-sm text-slate-400 mt-1">{b}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-2xl">
          <Badge className="bg-amber-50 text-amber-700 border border-amber-200 mb-3">Features</Badge>
          <h2 className="text-3xl lg:text-4xl font-bold font-heading tracking-tight">Everything you need to run the office</h2>
          <p className="mt-3 text-lg text-slate-600">One place for leads, customers, scheduling and follow-up — designed for the trades.</p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FEATURES.map((f) => (
            <Card key={f.title} className="p-6 border border-slate-200 hover:shadow-md transition-shadow" data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}>
              <div className="h-11 w-11 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
                <f.icon className="h-5 w-5 text-blue-600" />
              </div>
              <h3 className="font-heading font-semibold text-lg">{f.title}</h3>
              <p className="mt-2 text-slate-600 text-sm leading-relaxed">{f.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Badge className="bg-blue-50 text-blue-700 border border-blue-200 mb-3">How it works</Badge>
            <h2 className="text-3xl lg:text-4xl font-bold font-heading tracking-tight">Up and running in three steps</h2>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-8">
            {STEPS.map((s) => (
              <div key={s.n} className="relative">
                <div className="h-12 w-12 rounded-xl bg-blue-600 text-white font-heading font-bold text-xl flex items-center justify-center">{s.n}</div>
                <h3 className="mt-4 font-heading font-semibold text-xl">{s.title}</h3>
                <p className="mt-2 text-slate-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard preview */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 mb-3">Dashboard preview</Badge>
          <h2 className="text-3xl lg:text-4xl font-bold font-heading tracking-tight">See your whole business at a glance</h2>
          <p className="mt-3 text-lg text-slate-600">Track new leads, conversion rate, appointments and opportunity value — all in a clean dashboard that works on your phone in the field.</p>
          <ul className="mt-6 space-y-3">
            {["Real-time lead and pipeline stats", "Leads that need follow-up, flagged for you", "Recent activity across your team"].map((t) => (
              <li key={t} className="flex items-center gap-2 text-slate-700"><CheckCircle2 className="h-5 w-5 text-emerald-600" /> {t}</li>
            ))}
          </ul>
        </div>
        <div className="relative">
          <div className="absolute -inset-4 bg-gradient-to-tr from-blue-600/10 to-transparent blur-2xl rounded-3xl" />
          <img src={DASH} alt="Dashboard preview" className="relative rounded-2xl shadow-xl border border-slate-200 object-cover w-full h-[360px]" />
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <Badge className="bg-blue-500/20 text-blue-300 border border-blue-500/30 mb-3">Pricing</Badge>
            <h2 className="text-3xl lg:text-4xl font-bold font-heading tracking-tight">One simple plan</h2>
            <p className="mt-3 text-lg text-slate-400">Everything included. Cancel anytime.</p>
          </div>
          <div className="mt-12 max-w-md mx-auto">
            <Card className="p-8 bg-white text-slate-900 border-0 shadow-2xl">
              <div className="text-sm font-semibold text-blue-600 uppercase tracking-wider">PlumbFlow Pro</div>
              <div className="mt-3 flex items-end gap-1">
                <span className="text-5xl font-extrabold font-heading">$79</span>
                <span className="text-slate-500 mb-1">/month</span>
              </div>
              <ul className="mt-6 space-y-3">
                {["Unlimited leads & customers", "Public lead capture form", "Follow-up automation engine", "Appointments & calendar", "Analytics & reporting", "Unlimited team members", "Role-based access control"].map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm"><CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> {t}</li>
                ))}
              </ul>
              <Button onClick={() => navigate("/register")} data-testid="pricing-cta-btn" className="w-full mt-8 h-12 bg-blue-600 hover:bg-blue-700 text-base">
                Start your free trial
              </Button>
              <p className="text-center text-xs text-slate-500 mt-3">No credit card required to start</p>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-10">
          <Badge className="bg-amber-50 text-amber-700 border border-amber-200 mb-3">FAQ</Badge>
          <h2 className="text-3xl lg:text-4xl font-bold font-heading tracking-tight">Questions, answered</h2>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="text-left font-medium">{f.q}</AccordionTrigger>
              <AccordionContent className="text-slate-600">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-blue-600 text-white px-8 py-14 text-center relative overflow-hidden">
          <h2 className="text-3xl lg:text-4xl font-bold font-heading">Ready to book more plumbing jobs?</h2>
          <p className="mt-3 text-blue-100 text-lg max-w-xl mx-auto">Start your free trial today and never let another lead slip through the cracks.</p>
          <Button size="lg" onClick={() => navigate("/register")} data-testid="footer-cta-btn" className="mt-7 h-12 px-8 bg-white text-blue-700 hover:bg-blue-50 text-base">
            Get started free <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <Droplets className="h-5 w-5 text-blue-600" />
            <span className="font-heading font-bold text-slate-900">PlumbFlow</span>
          </div>
          <p>© {new Date().getFullYear()} PlumbFlow. Built for U.S. plumbing businesses.</p>
        </div>
      </footer>
    </div>
  );
}
